import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useMediaQuery } from 'react-responsive';
import NavbarVertical from './NavbarVertical';
import { Container } from 'react-bootstrap';
import ROUTES from '../../constants/routes';
import AccountMenu from './AccountMenu';
import Subscription from '../../models/Subscription';
import { useAuth } from '../../redux/ducks/auth';
import { useOrchestrator } from '../../redux/ducks/orchestrator';
import { isStringEmpty, isStringNotEmpty, isBooleanFalse, isBooleanTrue } from '../../utils/objectUtils';
import logger from '../../utils/logger';
import { BrandLoader } from '../../components/BrandLoader/BrandLoader';
import { DashboardPageHeader } from '../../components/DashboardPageHeader';

/**
 * Wraps V2 account routes with the responsive sidebar, page content container,
 * and outlet context needed by account dashboard pages.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.children] - Optional child route content to render before the outlet.
 * @param {string} [props.className] - Bootstrap spacing classes for the content container.
 * @param {boolean} [props.overflowHidden] - Whether to lock the page for full-height experiences such as chat.
 * @returns {JSX.Element} The V2 account dashboard layout.
 */
const AccountLayout = ({ children, className, overflowHidden }) => {
	const location = useLocation();
	const isMobile = useMediaQuery({ maxWidth: 767 });
	const { authContext, profile } = useAuth();
	const { programs } = useOrchestrator();

	const [showMenu, setShowMenu] = useState(false);
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const [subscriptions, setSubscriptions] = useState([]);
	const [loadedSubscriptionsUserId, setLoadedSubscriptionsUserId] = useState(null);

	// derived during render rather than set in the effect: a flag written by the effect
	// is still false on the render that consumes it, so route guards reading this from
	// the outlet context would decide on empty subscriptions before the fetch starts.
	const subscriptionsLoading = isStringNotEmpty(profile?.userId) && profile.userId !== loadedSubscriptionsUserId;

	useEffect(() => {
		if (isStringEmpty(profile?.userId)) {
			setSubscriptions([]);
			setLoadedSubscriptionsUserId(null);
			return;
		}
		let cancelled = false;
		const requestedUserId = profile.userId;
		Subscription.getByUserId(requestedUserId).then((list) => {
			if (isBooleanFalse(cancelled)) setSubscriptions(Array.isArray(list) ? list : []);
		}).catch((err) => {
			logger.logErrorEvent('DASHBOARD_V2_LAYOUT_SUBSCRIPTIONS_LOAD_ERROR', err, 'Failed to load dashboard subscriptions', true);
			if (isBooleanFalse(cancelled)) setSubscriptions([]);
		}).finally(() => {
			if (isBooleanFalse(cancelled)) setLoadedSubscriptionsUserId(requestedUserId);
		});
		return () => { cancelled = true; };
	}, [profile?.userId]);

	const handleToggleCollapse = () => {
		setSidebarCollapsed((prev) => !prev);
	};

	const sidebarWidth = isBooleanTrue(sidebarCollapsed) ? 'var(--rrx-sidebar-collapsed)' : 'var(--rrx-sidebar-expanded)';

	const isMessagesRoute = location.pathname === ROUTES.MY_MESSAGES;

	const pageContentStyle = isBooleanTrue(isMobile)
		? {
			width: '100%',
			minWidth: 0,
			minHeight: '100vh',
			overflowX: 'clip',
			...(isBooleanTrue(isMessagesRoute) ? { height: '100dvh', overflow: 'hidden' } : {}),
		}
		: {
			marginLeft: sidebarWidth,
			width: `calc(100% - ${sidebarWidth})`,
			transition: 'margin-left 550ms cubic-bezier(0.4, 0, 0.2, 1), width 450ms cubic-bezier(0.4, 0, 0.2, 1)',
			minHeight: '100vh',
			overflowX: 'clip',
			...(isBooleanTrue(isMessagesRoute) ? { height: '100dvh', minHeight: 0, overflow: 'hidden' } : {}),
		};

	const handleMenuToggle = () => {
		setShowMenu(isBooleanFalse(showMenu));
	};

	/**
	 * Injects the mobile menu toggle into nested dashboard page headers without
	 * requiring every V2 account page to thread the prop manually.
	 *
	 * @param {React.ReactElement} element - A rendered child element to inspect.
	 * @returns {React.ReactElement} The original or cloned element with menu toggle props.
	 */
	const injectMenuToggle = (element) => {
		if (!element) return element;
		if (element.type === DashboardPageHeader) {
			return React.cloneElement(element, { onMenuToggle: handleMenuToggle });
		}
		if (element.props?.children) {
			return React.cloneElement(element, {
				children: React.Children.map(element.props.children, injectMenuToggle)
			});
		}
		return element;
	};

	return (
		<div
			id="db-wrapper-v2"
			className={isBooleanTrue(overflowHidden) ? 'chat-layout' : ''}
			style={{ display: 'flex', minHeight: '100vh' }}
		>
			<NavbarVertical
				menuOptions={AccountMenu({ authContext, subscriptions, programs })}
				showMenu={isBooleanTrue(showMenu)}
				onClick={(value) => setShowMenu(value)}
				collapsed={isBooleanTrue(sidebarCollapsed)}
				onToggleCollapse={handleToggleCollapse}
			/>
			<main
				id="page-content-v2"
				className={`p-0 ${isBooleanTrue(overflowHidden) ? '' : ''}`}
				style={{ ...pageContentStyle, backgroundColor: 'var(--rrx-color-white-150)' }}
			>
				<Container fluid className={isBooleanTrue(isMessagesRoute) ? 'p-0' : (className ? className : 'p-4 pt-3')} style={isBooleanTrue(isMessagesRoute) ? { height: '100%' } : undefined}>
					{children && injectMenuToggle(children)}
					<Outlet context={{ onMenuToggle: handleMenuToggle, subscriptions, subscriptionsLoading }} />
				</Container>
			</main>
		</div>
	);
};
export default AccountLayout;
